<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-20">
    <div class="cf-flex cf-flex--column cf-flex--gap-8">
      <!-- Upsell Progress Bar -->
      <div
        v-if="hasUpsellTarget"
        class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center"
      >
        <div class="cf-upsell-progress-bar">
          <div
            class="cf-upsell-progress-bar__fill"
            :style="{ width: `${upsellStatus.progressPercent}` }"
          />
        </div>
      </div>

      <!-- Progress Status title -->
      <h2 class="cf-title cf-title--1">
        {{ upsellStatus.content.title }}
      </h2>

      <!-- Progress Status description -->
      <p class="cf-subtitle cf-subtitle--1">
        {{ upsellStatus.content.description }}
      </p>
    </div>

    <div
      v-for="upsellItemCategory in upsellOrderItems"
      :key="upsellItemCategory.menuCategoryId"
      class="cf-flex cf-flex--column cf-flex--gap-12 cf-text-align-left"
    >
      <!-- Upsell Item Category Title -->
      <h3
        v-if="isCategoryDisplayType"
        class="cf-title cf-title--3"
      >
        {{ upsellItemCategory.menuCategoryDisplayName }}
      </h3>

      <div class="cf-card-list">
        <!-- Upsell Item -->
        <cf-upsell-item
          v-for="item in upsellItemCategory.items"
          :key="item.menuItemId"
          :upsell-menu-item="item"
          :selected-upsell-items="selectedUpsellItems"
          :selected-upsell-item-count="selectedUpsellItemCount"
          @select-upsell-item="selectUpsellItemHandler"
          @unselect-upsell-item="unselectUpsellItemHandler"
        />
      </div>
    </div>

    <div class="cf-modal__footer cf-modal__footer--sticky">
      <!-- Add to cart primary cta -->
      <button
        :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', {
          'cf-button--loading': isLoading, 'cf-button--disabled': isAddToCartDisabled
        }]"
        type="submit"
        :disabled="isAddToCartDisabled"
        @click="addUpsellItemsClickHandler"
      >
        {{ upsellStatus.content.cta }}
      </button>

      <!-- Go to cart secondary cta, if user does not add upsell items -->
      <button
        :class="['cf-button cf-button--secondary cf-button--lg cf-button--fill', { 'cf-button--loading': isNavigatingToCart }]"
        type="submit"
        @click="navigateToCart"
      >
        {{ upsellStatus.content.secondaryCta }}
      </button>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import { goToOrderAheadCart } from '@/services/navigation/go-to-order-ahead-cart';

  import { goToOrderAtTableCart } from '@/services/navigation/go-to-order-at-table-cart';

  import errorHandlers from '@/services/error-handlers';

  import CfUpsellItem from './upsell-item/index.vue';


  export default {
    name: 'UpsellItems',

    components: {
      CfUpsellItem
    },

    data() {
      return {
        isLoading: false,
        currentUpsellTarget: {},
        nextUpsellTarget: {},
        isLastUpsellTarget: false,
        selectedUpsellItems: [],
        isNavigatingToCart: false
      };
    },

    computed: {
      ...mapGetters('orders', ['order', 'upsellItems']),
      ...mapGetters('menu', ['menu']),
      ...mapGetters('content', ['content']),
      ...mapGetters('session', ['session']),
      ...mapGetters('locations', ['selectedLocation']),

      isCategoryDisplayType() {
        return this.order.upsellDisplayMode === 'ModalCategory';
      },

      upsellOrderItems() {
        return this.upsellItems.modalUpsellItems;
      },

      selectedUpsellItemCount() {
        const count = this.selectedUpsellItems.reduce((acc, curr) => acc + curr.quantity, 0);

        return count;
      },

      cartItemQuantity() {
        let total = 0;

        this.order.orderItems.forEach((orderItem) => {
          if (orderItem.units) {
            total += orderItem.quantity * orderItem.units;
          }
          else {
            total += orderItem.quantity;
          }
        });

        return total + this.selectedUpsellItemCount;
      },

      upsellStatus() {
        if (!this.hasUpsellTarget) {
          return {
            content: {
              title: this.content.UpsellModal.title,
              description: this.content.UpsellModal.description,
              cta: this.content.UpsellModal.metadata.primaryCta,
              secondaryCta: this.content.UpsellModal.metadata.secondaryCta
            },
            progressPercent: '100%'
          };
        }

        /** Render succesful content if user's cart qty matches exact target amount */
        if (this.cartItemQuantity === this.currentUpsellTarget?.targetAmount) {
          return {
            content: {
              title: this.currentUpsellTarget.successTitle,
              description: this.currentUpsellTarget.successDescription,
              cta: this.currentUpsellTarget.primaryCta,
              secondaryCta: this.currentUpsellTarget.secondaryCta
            },
            progressPercent: '100%'
          };
        }

        if (this.cartItemQuantity > this.currentUpsellTarget?.targetAmount) {
          /**
           * Render succesful content if user's cart qty exceeds target amount AND is the last upsell target,
           * else return the next upsell target content.
           */
          if (this.isLastUpsellTarget) {
            return {
              content: {
                title: this.currentUpsellTarget.successTitle,
                description: this.currentUpsellTarget.successDescription,
                cta: this.currentUpsellTarget.primaryCta,
                secondaryCta: this.currentUpsellTarget.secondaryCta
              },
              progressPercent: '100%'
            };
          }

          const nextUpsellTargetQuantityProgress = this.getUpsellProgressBarStatus(this.nextUpsellTarget);

          return {
            content: {
              title: this.nextUpsellTarget.title,
              description: this.nextUpsellTarget.description,
              cta: this.nextUpsellTarget.primaryCta,
              secondaryCta: this.currentUpsellTarget.secondaryCta
            },
            progressPercent: `${nextUpsellTargetQuantityProgress}%`
          };
        }

        /** Render current upsell target content if user still has not met current target armount */
        const upsellQuantityProgress = this.getUpsellProgressBarStatus(this.currentUpsellTarget);

        return {
          content: {
            title: this.currentUpsellTarget.title,
            description: this.currentUpsellTarget.description,
            cta: this.currentUpsellTarget.primaryCta,
            secondaryCta: this.currentUpsellTarget.secondaryCta
          },
          progressPercent: `${upsellQuantityProgress}%`
        };
      },

      hasUpsellTarget() {
        return !!this.currentUpsellTarget;
      },

      isAddToCartDisabled() {
        return this.selectedUpsellItems.length === 0;
      },

      isOrderAtTable() {
        return this.session.orderDetails.orderType === 'table';
      }
    },

    watch: {
      /**
       * As a user updates their cart qty, watch to determine if new cart qty exceeds this.currentUpsellTarget.targetAmount that was initially defined on created().
       * If new qty exceeds current qty, update current upsell target with next upsell target, unless the current upsell target is the last target rule,
       * then use the last upsell target.
       */
      cartItemQuantity(newQty) {
        if (newQty > this.currentUpsellTarget?.targetAmount) {
          const lastUpsellTargetIndex = this.order.upsellRules.length - 1;

          // Need to check added qty against temp cart vs actual cart (ie. this.cartItemQuantity)
          const nextUpsellIdx = this.order.upsellRules?.findIndex(upsellRule => upsellRule?.targetAmount > this.cartItemQuantity);

          this.nextUpsellTarget = nextUpsellIdx !== -1
            ? this.order.upsellRules[nextUpsellIdx]
            : this.order.upsellRules[lastUpsellTargetIndex];

          this.isLastUpsellTarget = nextUpsellIdx === -1;

          this.currentUpsellTarget = this.nextUpsellTarget;
        }
      }
    },

    created() {
      this.onComponentCreated();
    },

    methods: {
      onComponentCreated() {
        /**
         * Get current upsell idx on created() in order to establish what the current target is before a user has updated their cart.
         * Use idx to get upsellRules value.
         */
        if (this.order.upsellRules) {
          const initialUpsellTarget = this.order.upsellRules.findIndex(upsellRule => upsellRule?.targetAmount > this.cartItemQuantity);

          this.currentUpsellTarget = this.order.upsellRules[initialUpsellTarget];
        }

        this.triggerGoogleEventTracking();
      },

      triggerGoogleEventTracking() {
        const eventData = { itemInfo: this.filteredUpsellItems, includeCurrency: true };

        this.$googleEventTracking.postEvent('view_item_list', eventData);
      },

      getUpsellProgressBarStatus(upsellTarget) {
        const progress = (this.cartItemQuantity / (upsellTarget?.targetAmount ?? 0)) * 100;

        return Math.round(progress);
      },

      selectUpsellItemHandler(upsellItem) {
        /** Find selected upsell items in list, if exists update its data, else push new entry */
        const matchedUpsellItem = this.selectedUpsellItems.findIndex(selectedItem => selectedItem.menuItemId === upsellItem.menuItemId);

        if (matchedUpsellItem !== -1) {
          // https://v2.vuejs.org/v2/guide/reactivity.html#For-Arrays
          this.selectedUpsellItems.splice(matchedUpsellItem, 1, upsellItem);
        }
        else {
          // https://v2.vuejs.org/v2/guide/reactivity.html#For-Arrays
          this.selectedUpsellItems = [...this.selectedUpsellItems, upsellItem];
        }
      },

      unselectUpsellItemHandler(upsellItem) {
        this.selectedUpsellItems = this.selectedUpsellItems.filter(selectedItem => selectedItem.menuItemId !== upsellItem.menuItemId);
      },

      async addUpsellItemsClickHandler() {
        try {
          this.isLoading = true;

          if (this.isOrderAtTable) {
            await this.$store.dispatch('orderAtTable/stageUpsellItems', this.selectedUpsellItems);
          }
          else {
            await this.$store.dispatch('orderAhead/addUpsellItemsToOrder', this.selectedUpsellItems);
          }

          this.isLoading = false;

          this.navigateToCart();
        }
        catch (error) {
          this.isLoading = false;

          errorHandlers.generic(error);
        }
      },

      navigateToCart() {
        this.isNavigatingToCart = true;

        this.$closeModal('upsellModal');

        if (this.isOrderAtTable) {
          goToOrderAtTableCart({ isUpsellOverride: true });
        }
        else {
          goToOrderAheadCart({ isUpsellOverride: true });
        }
      }
    }
  };
</script>
